import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import FileUploader from "@/components/FileUploader";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  name: "", builder: "", location: "", type: "Apartment", configurations: "", unit_sizes: "",
  starting_price: "", price_max: "", status: "Under Construction", possession: "", rera: "",
  highlights: "", overview: "", amenities: "", images: "", master_plan: "", brochure_url: "",
  featured: false, new_launch: false, trending: false, rera_approved: true, published: true,
};

const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toStr = (a) => (Array.isArray(a) ? a.join(", ") : a || "");

export default function AdminProjects() {
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, configurations: toStr(p.configurations), highlights: toStr(p.highlights), amenities: toStr(p.amenities), images: toStr(p.images) });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.builder || !form.location) { toast.error("Name, builder and location are required."); return; }
    setSaving(true);
    const payload = {
      ...form,
      starting_price: Number(form.starting_price) || 0,
      price_max: Number(form.price_max) || 0,
      configurations: toArr(form.configurations),
      highlights: toArr(form.highlights),
      amenities: toArr(form.amenities),
      images: toArr(form.images),
    };
    try {
      if (editing) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success(editing ? "Project updated" : "Project created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (p) => { await api.put(`/admin/projects/${p.id}`, { published: !p.published }); load(); };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; await api.delete(`/admin/projects/${p.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-navy">Projects</h1><p className="text-muted-foreground mt-1">Manage your property listings.</p></div>
        <Button onClick={openNew} data-testid="add-project-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Project</Button>
      </div>

      {projects === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-navy"><tr>
              <th className="text-left p-4">Project</th><th className="text-left p-4">Location</th><th className="text-left p-4">Price</th><th className="text-left p-4">Status</th><th className="text-left p-4">Tags</th><th className="text-right p-4">Actions</th>
            </tr></thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`project-row-${p.id}`}>
                  <td className="p-4"><div className="flex items-center gap-3">{p.images?.[0] ? <img src={p.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-muted" />}<div><p className="font-semibold text-navy">{p.name}</p><p className="text-xs text-muted-foreground">{p.builder}</p></div></div></td>
                  <td className="p-4 text-muted-foreground">{p.location}</td>
                  <td className="p-4 text-navy">₹{((p.starting_price || 0) / 100000).toFixed(0)}L</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${p.published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{p.published ? "Published" : "Draft"}</span></td>
                  <td className="p-4 text-xs text-gold-dark">{[p.featured && "Featured", p.new_launch && "New", p.trending && "Trending"].filter(Boolean).join(", ") || "-"}</td>
                  <td className="p-4"><div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => togglePublish(p)} data-testid={`toggle-publish-${p.id}`} title="Publish/Unpublish">{p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`delete-project-${p.id}`} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div></td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No projects yet. Click "Add Project".</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{editing ? "Edit Project" : "Add Project"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Project Name *" v={form.name} on={(v) => set("name", v)} testid="pf-name" />
            <Field label="Builder *" v={form.builder} on={(v) => set("builder", v)} testid="pf-builder" />
            <Field label="Location *" v={form.location} on={(v) => set("location", v)} testid="pf-location" />
            <Field label="Type (Apartment/Villa/Plot)" v={form.type} on={(v) => set("type", v)} testid="pf-type" />
            <Field label="Configurations (comma separated)" v={form.configurations} on={(v) => set("configurations", v)} testid="pf-config" />
            <Field label="Unit Sizes" v={form.unit_sizes} on={(v) => set("unit_sizes", v)} />
            <Field label="Starting Price (₹)" type="number" v={form.starting_price} on={(v) => set("starting_price", v)} testid="pf-price" />
            <Field label="Max Price (₹)" type="number" v={form.price_max} on={(v) => set("price_max", v)} />
            <Field label="Status" v={form.status} on={(v) => set("status", v)} />
            <Field label="Possession" v={form.possession} on={(v) => set("possession", v)} />
            <Field label="RERA Number" v={form.rera} on={(v) => set("rera", v)} />
          </div>
          <FileUploader mode="images" value={form.images} onChange={(v) => set("images", v)} label="Project Images (upload multiple)" />
          <FileUploader mode="image" value={form.master_plan} onChange={(v) => set("master_plan", v)} label="Master Plan Image" />
          <FileUploader mode="file" value={form.brochure_url} onChange={(v) => set("brochure_url", v)} label="Brochure (PDF)" />
          <Area label="Highlights (comma separated)" v={form.highlights} on={(v) => set("highlights", v)} />
          <Area label="Amenities (comma separated)" v={form.amenities} on={(v) => set("amenities", v)} />
          <Area label="Overview" v={form.overview} on={(v) => set("overview", v)} />
          <div className="flex flex-wrap gap-6">
            {[["featured", "Featured"], ["new_launch", "New Launch"], ["trending", "Trending"], ["rera_approved", "RERA Approved"], ["published", "Published"]].map(([k, l]) => (
              <div key={k} className="flex items-center gap-2"><Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pf-${k}`} /><Label className="text-sm">{l}</Label></div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-project-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold">{saving ? "Saving..." : "Save Project"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, type = "text", testid }) => (
  <div><Label className="text-xs font-semibold text-navy">{label}</Label><Input type={type} value={v} onChange={(e) => on(e.target.value)} className="bg-gray-50 mt-1" data-testid={testid} /></div>
);
const Area = ({ label, v, on, testid }) => (
  <div><Label className="text-xs font-semibold text-navy">{label}</Label><Textarea value={v} onChange={(e) => on(e.target.value)} className="bg-gray-50 mt-1" rows={2} data-testid={testid} /></div>
);
